/**
 * Route-level error boundary component that catches rendering errors
 * and displays fallback UI.
 *
 * @module store/ErrorBoundary
 * @see TASK-FRONTEND-WS-STATE-CHG-003
 * @see TASK-BUGFIX-STALE-CHUNK-ERROR-PAGE-CHG-005
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorFallback } from "../components/shared/RouteErrorBoundary";
import { isChunkLoadError, reloadForStaleChunk } from "../utils/staleChunkRecovery";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback UI. Receives the error and a reset function. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Called when an error is caught. Useful for logging. */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Error Boundary
// ---------------------------------------------------------------------------

/**
 * React error boundary for catching rendering errors at the route level.
 * Displays a fallback UI and provides a reset mechanism.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.onError?.(error, errorInfo);
    if (isChunkLoadError(error)) {
      console.error("[ErrorBoundary] lazy chunk failed to load:", error);
      reloadForStaleChunk();
    }
  }

  private handleReset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }
      return (
        <ErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
